import {Trans} from '@lingui/macro';
import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {AbsoluteDateTimeFormatterContainer} from '../../../../../../../../../srcCommon/formatters/dateTime/absoluteDateTimeFormatterContainer';
import {Icon} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../../../shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {InfoBox} from '../../../common/infobox/infoBox';
import {useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsScheduleInfoBoxContainerProps} from './crmSyncsScheduleInfoBoxContainer';

/*
 * Constants.
 */

const hoursTillNextSyncFromPrevious = 24;
const maxTooltipWidth = 305; // px.
const iconName = 'repeatCircleSmall';

/*
 * Styles.
 */

const StyledTitleDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledSyncDateTitleDiv = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  margin-bottom: 4px;
`;

const StyledSyncDateDiv = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  margin-bottom: 12px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

interface StyledSyncInProgressWrapperDivProps {
  $progressPercentage: number;
}

const StyledSyncInProgressWrapperDiv = styled.div<StyledSyncInProgressWrapperDivProps>`
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 16px;
  border: 1px solid ${(p) => p.theme.greys.shade20};
  background: linear-gradient(
    to right,
    ${(p) => `${p.theme.greys.white} ${p.$progressPercentage}%`},
    ${(p) => `${p.theme.greys.shade20} ${p.$progressPercentage}%`}
  );
  padding: 16px;
`;

const StyledSyncInProgressTitleTextDiv = styled.div`
  font-size: 14px;
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledSyncInProgressProgressTextDiv = styled.div`
  font-size: 12px;
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
  text-align: right;
  flex: 1;
`;

const StyledUnorderedList = styled.ul`
  font-size: 12px;
  line-height: 150%;
  padding: 0 0 0 18px;
  margin: 0;
  color: ${(p) => p.theme.greys.white};
`;

const StyledListItem = styled.li`
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
`;

/*
 * Component.
 */

export const CrmSyncsScheduleInfoBox: FC<CrmSyncsScheduleInfoBoxContainerProps> = (props) => {
  const {lastSyncedAt, showNextSync, source, jobProgressPercentage, canSyncNow, onSync} = props;
  const {displayName} = useCrmPageConfig(source);

  if (!lastSyncedAt) {
    return null;
  }

  if (jobProgressPercentage !== undefined) {
    return renderSyncInProgress(displayName, jobProgressPercentage);
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <InfoBox
          iconName={iconName}
          backgroundColor={theme.greys.white}
          borderColor={theme.greys.shade30}
          title={
            <StyledTitleDiv>
              <Trans id="crm-sync-page-sync-box-title">Sync schedule</Trans>
              <TooltipCoordinator
                placement="top"
                persistent
                maxWidth={maxTooltipWidth}
                delay={0}
                render={() => renderTooltipContent(displayName)}
              />
            </StyledTitleDiv>
          }
          footer={<Trans id="crm-sync-page-sync-box-sync-link">Sync now</Trans>}
          onFooterClick={onSync}
          isExternalLink={false}
          isFooterDisabled={!canSyncNow}
        >
          <StyledSyncDateTitleDiv>
            <Trans id="crm-sync-page-sync-box-last-sync">Last sync</Trans>
          </StyledSyncDateTitleDiv>
          <StyledSyncDateDiv>
            <AbsoluteDateTimeFormatterContainer value={lastSyncedAt} />
          </StyledSyncDateDiv>
          {showNextSync && (
            <>
              <StyledSyncDateTitleDiv>
                <Trans id="crm-sync-page-sync-box-next-sync">Next sync</Trans>
              </StyledSyncDateTitleDiv>
              <StyledSyncDateDiv>
                <AbsoluteDateTimeFormatterContainer
                  value={DateTime.fromMillis(lastSyncedAt)
                    .plus({hours: hoursTillNextSyncFromPrevious})
                    .valueOf()}
                />
              </StyledSyncDateDiv>
            </>
          )}
        </InfoBox>
      )}
    </UserThemeConsumer>
  );
};

/*
 * Helpers.
 */

function renderSyncInProgress(displayName: string, progressPercentage: number) {
  return (
    <StyledSyncInProgressWrapperDiv $progressPercentage={progressPercentage}>
      <Icon name={iconName} />
      <StyledSyncInProgressTitleTextDiv>
        <Trans id="crm-sync-page-sync-box-in-progress-title">Sync in progress...</Trans>
      </StyledSyncInProgressTitleTextDiv>
      <TooltipCoordinator
        placement="top"
        persistent
        maxWidth={maxTooltipWidth}
        delay={0}
        render={() => renderTooltipContent(displayName)}
      />
      <StyledSyncInProgressProgressTextDiv>{progressPercentage}%</StyledSyncInProgressProgressTextDiv>
    </StyledSyncInProgressWrapperDiv>
  );
}

function renderTooltipContent(syncDisplayName: string) {
  return (
    <StyledUnorderedList>
      <StyledListItem>
        <Trans id="crm-syncs-sync-schedule-info-box-tooltip-one-way-sync">
          One-way sync from {syncDisplayName} to Front
        </Trans>
      </StyledListItem>
      <StyledListItem>
        <Trans id="crm-syncs-sync-schedule-info-box-tooltip-timing-sync">
          Repeats every 24 hours after last manual sync
        </Trans>
      </StyledListItem>
    </StyledUnorderedList>
  );
}
